import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import { MapPin, Plus, Pencil, Trash2, Cpu, Building2, Info } from 'lucide-react';
import { toast } from 'sonner';

export default function Areas() {
    const { api, isAdmin } = useAuth();
    const [areas, setAreas] = useState([]);
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingArea, setEditingArea] = useState(null);
    
    const [formData, setFormData] = useState({
        name: '',
        description: ''
    });

    const fetchData = async () => {
        try {
            const [areasRes, devicesRes] = await Promise.all([
                api.get('/areas'),
                api.get('/devices')
            ]);
            setAreas(areasRes.data);
            setDevices(devicesRes.data);
        } catch (error) {
            console.error('Error fetching areas:', error);
            toast.error('Erro ao carregar áreas');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const resetForm = () => {
        setFormData({ name: '', description: '' });
        setEditingArea(null);
    };

    const handleOpenDialog = (area = null) => {
        if (area) {
            setEditingArea(area);
            setFormData({
                name: area.name,
                description: area.description || ''
            });
        } else {
            resetForm();
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        resetForm();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (editingArea) {
                await api.put(`/areas/${editingArea.id}`, formData);
                toast.success('Área atualizada com sucesso');
            } else {
                await api.post('/areas', formData);
                toast.success('Área criada com sucesso');
            }
            
            handleCloseDialog();
            fetchData();
        } catch (error) {
            console.error('Error saving area:', error);
            toast.error(error.response?.data?.detail || 'Erro ao guardar área');
        }
    };

    const handleDelete = async (areaId) => {
        try {
            await api.delete(`/areas/${areaId}`);
            toast.success('Área eliminada com sucesso');
            fetchData();
        } catch (error) {
            console.error('Error deleting area:', error);
            toast.error(error.response?.data?.detail || 'Erro ao eliminar área');
        }
    };

    const getAreaDeviceCount = (areaId) => {
        return devices.filter(d => d.area_id === areaId).length;
    };

    const getAreaTotalPower = (areaId) => {
        const areaDevices = devices.filter(d => d.area_id === areaId);
        return areaDevices.reduce((sum, d) => sum + (d.last_reading?.power || 0), 0);
    };

    const formatPower = (value) => {
        if (value >= 1000) return `${(value / 1000).toFixed(2)} kW`;
        return `${value.toFixed(0)} W`;
    };

    if (loading) {
        return (
            <div className="space-y-6" data-testid="areas-loading">
                <div className="flex justify-between items-center">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-10 w-36" />
                </div>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {[...Array(6)].map((_, i) => (
                        <Skeleton key={i} className="h-40 rounded-lg" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="areas-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold font-heading text-slate-900">Áreas</h1>
                    <p className="text-slate-500">
                        {isAdmin 
                            ? 'Organize os dispositivos por áreas físicas' 
                            : 'Visualize as áreas físicas da empresa'}
                    </p>
                </div>
                {isAdmin && (
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleOpenDialog()}
                                data-testid="add-area-btn"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Criar Área
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                                <DialogTitle className="font-heading">
                                    {editingArea ? 'Editar Área' : 'Criar Área'}
                                </DialogTitle>
                                <DialogDescription>
                                    {editingArea ? 'Atualize os dados da área' : 'Crie uma nova área física para organizar os seus dispositivos'}
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Nome da Área</Label>
                                    <Input
                                        id="name"
                                        placeholder="Ex: Fábrica Principal"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                        data-testid="area-name-input"
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="description">Descrição (opcional)</Label>
                                    <Textarea
                                        id="description"
                                        placeholder="Descreva esta área..."
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        rows={3}
                                        data-testid="area-description-input"
                                    />
                                </div>

                                <DialogFooter>
                                    <Button type="button" variant="outline" onClick={handleCloseDialog}>
                                        Cancelar
                                    </Button>
                                    <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="area-save-btn">
                                        {editingArea ? 'Guardar' : 'Criar'}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* Info alert for regular users */}
            {!isAdmin && (
                <Alert>
                    <Info className="w-4 h-4" />
                    <AlertDescription>
                        Apenas administradores podem criar, editar ou eliminar áreas. 
                        Contacte o administrador da sua empresa para fazer alterações.
                    </AlertDescription>
                </Alert>
            )}

            {/* Areas Grid */}
            {areas.length === 0 ? (
                <Card className="p-12" data-testid="no-areas">
                    <div className="text-center">
                        <MapPin className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">Nenhuma área configurada</h3>
                        <p className="text-slate-500 mb-6 max-w-md mx-auto">
                            {isAdmin 
                                ? 'Crie áreas físicas como Fábrica, Armazém, Escritórios para organizar os seus dispositivos.'
                                : 'Não existem áreas configuradas. Contacte o administrador para criar áreas.'
                            }
                        </p>
                        {isAdmin && (
                            <Button
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleOpenDialog()}
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Criar Primeira Área
                            </Button>
                        )}
                    </div>
                </Card>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {areas.map((area) => {
                        const deviceCount = getAreaDeviceCount(area.id);
                        const totalPower = getAreaTotalPower(area.id);
                        
                        return (
                            <Card key={area.id} className="hover:shadow-md transition-shadow" data-testid={`area-card-${area.id}`}>
                                <CardHeader className="pb-3">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-slate-100">
                                                <Building2 className="w-5 h-5 text-slate-600" />
                                            </div>
                                            <div>
                                                <CardTitle className="text-base font-heading">{area.name}</CardTitle>
                                                {area.description && (
                                                    <CardDescription className="text-xs mt-1 line-clamp-1">
                                                        {area.description}
                                                    </CardDescription>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="flex items-center gap-2">
                                                <Cpu className="w-4 h-4 text-slate-400" />
                                                <div>
                                                    <p className="text-xs text-slate-500">Dispositivos</p>
                                                    <p className="font-semibold text-slate-900">{deviceCount}</p>
                                                </div>
                                            </div>
                                            <div>
                                                <p className="text-xs text-slate-500">Potência Total</p>
                                                <p className="font-semibold text-slate-900">{formatPower(totalPower)}</p>
                                            </div>
                                        </div>
                                        
                                        {isAdmin && (
                                            <div className="flex items-center gap-2 pt-2 border-t">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleOpenDialog(area)}
                                                    data-testid={`edit-area-${area.id}`}
                                                >
                                                    <Pencil className="w-4 h-4 mr-1" />
                                                    Editar
                                                </Button>
                                                <AlertDialog>
                                                    <AlertDialogTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                            data-testid={`delete-area-${area.id}`}
                                                        >
                                                            <Trash2 className="w-4 h-4 mr-1" />
                                                            Eliminar
                                                        </Button>
                                                    </AlertDialogTrigger>
                                                    <AlertDialogContent>
                                                        <AlertDialogHeader>
                                                            <AlertDialogTitle>Eliminar Área?</AlertDialogTitle>
                                                            <AlertDialogDescription>
                                                                Esta ação não pode ser revertida. Os dispositivos associados a esta área ficarão sem área atribuída.
                                                            </AlertDialogDescription>
                                                        </AlertDialogHeader>
                                                        <AlertDialogFooter>
                                                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                                            <AlertDialogAction
                                                                className="bg-red-600 hover:bg-red-700"
                                                                onClick={() => handleDelete(area.id)}
                                                            >
                                                                Eliminar
                                                            </AlertDialogAction>
                                                        </AlertDialogFooter>
                                                    </AlertDialogContent>
                                                </AlertDialog>
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
